import { List, useTable, EditButton } from '@refinedev/antd';
import { Table, Tag, Button, Popconfirm, Space, message } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { LockOutlined, DeleteOutlined } from '@ant-design/icons';
import { authClient } from '../../providers/axios';
import { AxiosError } from 'axios';
import { canEditRole } from './utils/systemRoles';
import { usersWithRolePath } from './utils/usersWithRolePath';
import { useRoleAccess, canSaveInCompanyLimit } from './utils/roleAccess';
import { IdentityErrorAlert } from './components/IdentityErrorAlert';

interface Role {
  id: string;
  _id?: string;
  name: string;
  description?: string;
  scope?: 'internal' | 'customer';
  companyId?: string | null;
  companyName?: string | null;
  permissions: string[];
  isSystem?: boolean;
  userCount?: number;
}

export const RolesListPage: React.FC = () => {
  const navigate = useNavigate();
  const access = useRoleAccess();

  const { tableProps, tableQuery } = useTable<Role>({
    resource: 'roles',
    meta: { dataProviderName: 'authDataProvider' },
    syncWithLocation: true,
    pagination: { pageSize: 10 },
  });

  const handleDelete = async (record: Role) => {
    try {
      await authClient.delete(`/roles/${record.id || record._id}`);
      message.success('Role deleted');
      tableQuery?.refetch();
    } catch (err) {
      const axiosErr = err as AxiosError<{ message?: string }>;
      if (axiosErr.response?.status === 409) {
        message.error(axiosErr.response.data?.message || 'Role is in use and cannot be deleted');
      } else {
        message.error('Failed to delete role');
      }
    }
  };

  return (
    <List title="Roles" createButtonProps={{}}>
      <IdentityErrorAlert access={access} />
      <Table
        {...tableProps}
        rowKey={(record) => record.id || record._id || record.name}
        onRow={(record) => ({
          onClick: () => navigate(`/roles/${record.id || record._id}`),
          style: { cursor: 'pointer' },
        })}
      >
        <Table.Column dataIndex="name" title="Role Name" sorter />
        <Table.Column
          dataIndex="description"
          title="Description"
          render={(desc: string) => desc || '—'}
        />
        <Table.Column
          dataIndex="scope"
          title="Scope"
          render={(scope: string) => {
            const s = scope ?? 'internal';
            return <Tag color={s === 'customer' ? 'orange' : 'purple'}>{s}</Tag>;
          }}
        />
        <Table.Column
          dataIndex="companyName"
          title="Company"
          render={(name: string) => name || '—'}
        />
        <Table.Column
          dataIndex="permissions"
          title="Permissions"
          render={(permissions: string[]) => <Tag color="blue">{permissions?.length ?? 0}</Tag>}
        />
        <Table.Column
          dataIndex="userCount"
          title="Users"
          render={(count: number | undefined, record: Role) =>
            typeof count === 'number' && !access.canOpenUsers ? (
              // The people list needs admin:users:read, so show the number without a link.
              count
            ) : typeof count === 'number' ? (
              <Link
                to={usersWithRolePath(record.name)}
                title="See the people with this role"
                // The row opens the role; the number opens its people.
                onClick={(e) => e.stopPropagation()}
              >
                {count}
              </Link>
            ) : (
              '—'
            )
          }
        />
        <Table.Column
          title="Actions"
          render={(_: unknown, record: Role) => {
            const outsideLimit = !canSaveInCompanyLimit(record, access.limitedCompanies);
            return (
              <Space onClick={(e) => e.stopPropagation()}>
                <EditButton
                  resource="roles"
                  recordItemId={record.id || record._id}
                  disabled={!canEditRole(record) || outsideLimit}
                  icon={!canEditRole(record) || outsideLimit ? <LockOutlined /> : undefined}
                  title={
                    !canEditRole(record)
                      ? 'The super admin role already has every permission'
                      : outsideLimit
                        ? 'You can only change roles for your own companies'
                        : record.isSystem
                          ? 'Add permissions to this built-in role'
                          : 'Edit'
                  }
                />
                {/* Deleting a role needs admin:roles:*; without it there is no Delete at all. */}
                {access.canDelete && (
                  <Popconfirm
                    title="Are you sure you want to delete this role?"
                    onConfirm={() => handleDelete(record)}
                    okText="Yes"
                    cancelText="No"
                    disabled={!!record.isSystem}
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      disabled={!!record.isSystem}
                      title={record.isSystem ? 'System roles cannot be deleted' : 'Delete'}
                      size="small"
                    />
                  </Popconfirm>
                )}
              </Space>
            );
          }}
        />
      </Table>
    </List>
  );
};
